import { motion } from "framer-motion";
import { CASES } from "@/constants/testIds";
import TiltCard from "@/components/TiltCard";
import Divider from "@/components/Divider";
import { TrendingUp, ExternalLink } from "lucide-react";

export default function CaseStudies({ items = [] }) {
    return (
        <section
            id="work"
            data-testid={CASES.root}
            className="relative py-20 md:py-28"
        >
            <Divider label="proof" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                    <div className="max-w-2xl">
                        <p className="text-xs uppercase tracking-[0.35em] text-[var(--accent-gold)] mb-5">
                            Case Studies
                        </p>
                        <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[var(--text-primary)]">
                            Numbers that{" "}
                            <span className="italic text-[var(--accent-champagne)]">
                                don&apos;t lie.
                            </span>
                        </h2>
                    </div>
                    <p className="text-[var(--text-secondary)] max-w-md">
                        Real accounts. Real growth. Zero ads. Names withheld where clients
                        prefer privacy. The numbers speak.
                    </p>
                </div>

                <div
                    data-testid={CASES.grid}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 perspective-1000"
                >
                    {items.map((cs, i) => (
                        <motion.div
                            key={cs.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
                        >
                            <TiltCard intensity={7} lift={10}>
                                <div
                                    data-testid={CASES.card(cs.id)}
                                    className="group relative h-full rounded-2xl overflow-hidden border border-[var(--border-subtle)] bg-[rgba(0,0,0,0.35)] hover:border-[var(--border-accent)] transition-all"
                                >
                                    {cs.image_url ? (
                                        <div className="relative h-40 md:h-44 overflow-hidden">
                                            <img
                                                src={cs.image_url}
                                                alt={cs.client_type}
                                                className="w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[rgba(74,5,5,0.5)] to-[#3B0404]" />
                                            <div className="absolute top-4 left-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[var(--accent-champagne)]">
                                                <span className="w-2 h-2 rounded-full bg-[var(--accent-gold)] soft-pulse" />
                                                {cs.industry}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="h-32 bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-dark)]" />
                                    )}

                                    <div className="p-6 md:p-7">
                                        <p className="font-display font-bold text-3xl md:text-4xl gold-shimmer leading-none">
                                            {cs.hero_stat}
                                        </p>
                                        <p className="mt-4 text-[var(--text-primary)] font-semibold">
                                            {cs.client_type}
                                        </p>
                                        <p className="mt-2 text-sm text-[var(--text-secondary)] leading-relaxed">
                                            {cs.description}
                                        </p>
                                        <div className="mt-5 pt-5 border-t border-[var(--border-subtle)] flex items-center justify-between text-xs uppercase tracking-[0.2em]">
                                            <span className="flex items-center gap-2 text-[var(--accent-champagne)]/80">
                                                <TrendingUp size={14} /> Organic
                                            </span>
                                            <span className="flex items-center gap-1.5 text-[var(--text-secondary)]/70">
                                                Screenshot placeholder{" "}
                                                <ExternalLink size={11} />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </TiltCard>
                        </motion.div>
                    ))}
                </div>

                {items.length === 0 && (
                    <p className="text-center text-[var(--text-secondary)] py-16">
                        Case studies loading…
                    </p>
                )}
            </div>
        </section>
    );
}
